import { useEffect, useState } from "react";

/**
 * XpBurst — global, non-blocking floating "+N PD" toasts that pop into the
 * top-right corner every time a package is delivered. Listens on a window
 * CustomEvent so ANY component can trigger a burst without prop drilling:
 *
 *     import { emitXp } from "@/components/XpBurst";
 *     emitXp(1);   // → shows "+1 PD"
 *
 * Design goals:
 *   • Never blocks pointer events (fully `pointer-events-none`).
 *   • Stacks cleanly when the driver flies through 10 packages in a row.
 *   • Auto-removes each pill after ~1.2s so state stays tiny.
 *   • Sits below the app header (top: 5rem) so it doesn't cover branding.
 */
const XP_EVENT = "xp:gain";

export function emitXp(amount = 1) {
  window.dispatchEvent(new CustomEvent(XP_EVENT, { detail: { amount } }));
}

export default function XpBurst() {
  const [bursts, setBursts] = useState([]);

  useEffect(() => {
    const onGain = (e) => {
      const amount = Math.max(1, Number(e?.detail?.amount) || 1);
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      // Stagger vertical position slightly so overlapping bursts don't
      // stack perfectly on top of each other — feels more organic.
      const offset = Math.random() * 12 - 6;
      setBursts((prev) => [...prev, { id, amount, offset }].slice(-8));
      window.setTimeout(() => {
        setBursts((prev) => prev.filter((b) => b.id !== id));
      }, 1250);
    };
    window.addEventListener(XP_EVENT, onGain);
    return () => window.removeEventListener(XP_EVENT, onGain);
  }, []);

  if (bursts.length === 0) return null;

  return (
    <div
      className="fixed top-20 right-4 z-[1400] pointer-events-none flex flex-col items-end gap-1"
      data-testid="xp-burst-layer"
      aria-hidden
    >
      {bursts.map((b) => (
        <div
          key={b.id}
          data-testid="xp-burst-item"
          className="xp-burst px-3 py-1.5 rounded-full bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-400 text-white text-xs font-heading font-black uppercase tracking-widest shadow-[0_8px_24px_-6px_rgba(245,158,11,0.7)] ring-1 ring-white/25"
          style={{ marginRight: `${b.offset}px` }}
        >
          +{b.amount} PD
        </div>
      ))}
    </div>
  );
}
